/**
 * Minimal WhatsApp-style formatter for the webchat widget.
 *
 * The flow engine tells the model to format replies with WhatsApp markers
 * (*bold*, _italic_, ~strike~, `mono`, ```block```). The widget used to dump
 * that text raw, so visitors saw the markers literally. This helper converts
 * those markers into React nodes and preserves newlines / plain URLs.
 *
 * Intentionally non-ambitious: no nesting support, no autolink for emails,
 * no HTML sanitation beyond rejecting JSX (we build nodes by hand). Matches
 * the behavior WhatsApp itself gives for simple user-authored messages.
 */

import { Fragment, ReactNode } from 'react';

interface InlinePattern {
  regex: RegExp;
  render: (inner: string, key: string) => ReactNode;
}

// Order matters: code block first (greedy triple backtick), then single-char.
const INLINE_PATTERNS: InlinePattern[] = [
  {
    regex: /```([\s\S]+?)```/g,
    render: (inner, key) => (
      <code
        key={key}
        className="block bg-gray-100 text-gray-800 rounded px-2 py-1 font-mono text-[12px] whitespace-pre-wrap my-1"
      >
        {inner}
      </code>
    ),
  },
  {
    regex: /`([^`\n]+?)`/g,
    render: (inner, key) => (
      <code
        key={key}
        className="bg-gray-100 text-gray-800 rounded px-1 font-mono text-[12px]"
      >
        {inner}
      </code>
    ),
  },
  {
    regex: /\*([^*\n]+?)\*/g,
    render: (inner, key) => (
      <strong key={key} className="font-semibold">
        {inner}
      </strong>
    ),
  },
  {
    regex: /_([^_\n]+?)_/g,
    render: (inner, key) => (
      <em key={key} className="italic">
        {inner}
      </em>
    ),
  },
  {
    regex: /~([^~\n]+?)~/g,
    render: (inner, key) => (
      <s key={key} className="line-through">
        {inner}
      </s>
    ),
  },
];

const URL_REGEX = /\b(https?:\/\/[^\s<>"']+)/g;

interface Match {
  start: number;
  end: number;
  node: ReactNode;
}

function findFirstMatch(text: string, offset: number, keyPrefix: string): Match | null {
  let best: Match | null = null;

  for (let i = 0; i < INLINE_PATTERNS.length; i += 1) {
    const { regex, render } = INLINE_PATTERNS[i];
    regex.lastIndex = 0;
    const match = regex.exec(text);
    if (!match) continue;
    const start = match.index;
    const end = start + match[0].length;
    if (!best || start < best.start) {
      best = {
        start,
        end,
        node: render(match[1], `${keyPrefix}-${i}-${offset + start}`),
      };
    }
  }

  return best;
}

function linkifyPlain(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <a
        key={`${keyPrefix}-link-${match.index}`}
        href={match[0]}
        target="_blank"
        rel="noopener noreferrer"
        className="underline break-all"
      >
        {match[0]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

export function renderWhatsAppMarkup(text: string): ReactNode {
  if (!text) return null;
  const out: ReactNode[] = [];
  let cursor = 0;
  let guard = 0;

  while (cursor < text.length && guard < 500) {
    guard += 1;
    const slice = text.slice(cursor);
    const match = findFirstMatch(slice, cursor, `wa-${cursor}`);
    if (!match) {
      out.push(...linkifyPlain(slice, `wa-tail-${cursor}`));
      break;
    }
    if (match.start > 0) {
      out.push(...linkifyPlain(slice.slice(0, match.start), `wa-pre-${cursor}`));
    }
    out.push(match.node);
    cursor += match.end;
  }

  return <Fragment>{out}</Fragment>;
}
